import { TableRow, TableCell, IconButton, Typography } from "@mui/material"
import { Delete } from "@mui/icons-material"
import { capitalize } from "lodash"
import classnames from "classnames"
import OPTooltip from "components/material/tooltip"
import { factorRuleMaxValidator, factorRuleMinValidator } from "connected-components/optimize-experiments/form-validation-schemas/rules"
import { type OptimizeFormApi, useFormContext } from "connected-components/optimize-experiments/form-context"
import { FACTOR_RULE_TYPES } from "components/search-factor/constants"
import type { FactorRuleDisplay, FactorRuleRowProps } from "../types"
import FactorRuleInput from "./input"

const CELL_CONFIG = [
  {
    id: "factor",
    render: (rule: FactorRuleDisplay) => ({
      content: rule.name,
    }),
  },
  {
    id: "dataset",
    render: (rule: FactorRuleDisplay) => {
      let dataset
      if (rule.type === FACTOR_RULE_TYPES.CLASSIFICATION) {
        dataset = rule.groupById
      } else if (rule.type === FACTOR_RULE_TYPES.FACTOR) {
        dataset = "Risk factor"
      } else if (rule.contentSetId) {
        dataset = rule.contentSetId
      } else {
        dataset = capitalize(rule.category)
      }
      return {
        content: dataset,
      }
    },
  },
  {
    id: "current",
    render: (rule: Partial<FactorRuleDisplay>) => ({
      content: rule.value ?? "-",
    }),
  },
] as const

function parseFieldValue(value: string | undefined) {
  const parsedValue = value ? parseFloat(value) : null
  return isNaN(parsedValue as number) || parsedValue === null ? null : parsedValue
}

export default function FactorRuleRow({ rule, index, ruleDisplayType, handleUpdateRule, handleRemoveRule }: FactorRuleRowProps) {
  const form: OptimizeFormApi = useFormContext()

  return (
    <form.Subscribe
      selector={(state) => ({
        minError: state.fieldMeta[`rules.${ruleDisplayType}[${index}].min`]?.errors?.[0],
        maxError: state.fieldMeta[`rules.${ruleDisplayType}[${index}].max`]?.errors?.[0],
      })}
    >
      {({ minError, maxError }) => {
        const minMaxValueError = minError || maxError
        return (
          <>
            <TableRow className={classnames("table-row", { "has-error": minMaxValueError })}>
              {CELL_CONFIG.map((cell) => {
                const { content } = cell.render(rule)
                if (cell.id === "factor" || cell.id === "dataset") {
                  return (
                    <TableCell className="table-cell" key={cell.id}>
                      <OPTooltip title={String(content || "")}>
                        <span>{content}</span>
                      </OPTooltip>
                    </TableCell>
                  )
                }
                return (
                  <TableCell className="table-cell" key={cell.id}>
                    {content}
                  </TableCell>
                )
              })}
              <form.Field
                name={`rules.${ruleDisplayType}[${index}].min`}
                validators={{
                  onChangeListenTo: [`rules.${ruleDisplayType}[${index}].max`],
                  onChange: factorRuleMinValidator(ruleDisplayType, index),
                  onMount: factorRuleMinValidator(ruleDisplayType, index),
                }}
              >
                {(field) => (
                  <TableCell className="table-cell">
                    <FactorRuleInput
                      ruleId={rule.id}
                      field="min"
                      value={field.state.value?.toString() ?? ""}
                      onChange={(value: string | undefined) => {
                        field.handleChange(parseFieldValue(value))
                      }}
                      onBlur={(value: string | undefined) => {
                        field.handleBlur()
                        handleUpdateRule(rule, "min", value ?? "", ruleDisplayType)
                      }}
                    />
                  </TableCell>
                )}
              </form.Field>
              <form.Field
                name={`rules.${ruleDisplayType}[${index}].max`}
                validators={{
                  onChangeListenTo: [`rules.${ruleDisplayType}[${index}].min`],
                  onChange: factorRuleMaxValidator(ruleDisplayType, index),
                  onMount: factorRuleMaxValidator(ruleDisplayType, index),
                }}
              >
                {(field) => (
                  <TableCell className="table-cell">
                    <FactorRuleInput
                      ruleId={rule.id}
                      field="max"
                      value={field.state.value?.toString() ?? ""}
                      onChange={(value: string | undefined) => {
                        field.handleChange(parseFieldValue(value))
                      }}
                      onBlur={(value: string | undefined) => {
                        field.handleBlur()
                        handleUpdateRule(rule, "max", value ?? "", ruleDisplayType)
                      }}
                    />
                  </TableCell>
                )}
              </form.Field>
              <TableCell className="table-cell">
                <IconButton
                  aria-label="Delete"
                  onClick={() => handleRemoveRule(rule, ruleDisplayType)}
                  size="small"
                >
                  <Delete fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
            {minMaxValueError && (
              <TableRow className="error-row">
                <TableCell colSpan={6}>
                  <Typography variant="caption" color="error">
                    {minMaxValueError}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </>
          )
        }}
    </form.Subscribe>
  )
}
